<script setup lang="ts">
import { computed } from 'vue';

import type { FormSchema } from '@modules/forms/components/VForm/types';
import type { IFormSelectInputOption } from '@modules/forms/types';
import type {
  ISettingsClickDomainsItemCreated,
  ISettingsClickDomainsSetDefaultPayload,
} from '@modules/general-settings/submodules/click-domains/types';
import VButton from '@components/buttons/VButton/VButton.vue';
import VCard from '@components/card/VCard/VCard.vue';
import VLoader from '@components/loader/VLoader.vue';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { TRANSLATION_GROUP_GENERAL_SETTINGS } from '@modules/general-settings/constants';
import {
  ADMIN_GENERAL_SETTINGS_CLICK_DOMAINS_ADD_ROUTE_NAME,
  TRANSLATION_GROUP_GENERAL_SETTINGS_CLICK_DOMAINS,
} from '@modules/general-settings/submodules/click-domains/constants';
import {
  useSettingsClickDomainsSetDefaultMutation,
} from '@modules/general-settings/submodules/click-domains/mutations';
import useSettingsClickDomainsListQuery
  from '@modules/general-settings/submodules/click-domains/queries/useSettingsClickDomainsListQuery';
import { useI18n } from '@modules/i18n/composables';

const { getTranslation } = useI18n();

const { data, isLoading } = useSettingsClickDomainsListQuery();
const { mutateAsync: setDefaultDomain, errors, isLoading: isLoadingSubmission } = useSettingsClickDomainsSetDefaultMutation();

const domainOptions = computed<IFormSelectInputOption<ISettingsClickDomainsItemCreated['id']>[]>(() => data.value?.map(
  (a) => ({ value: a.id, label: a.domain }),
) ?? []);
const defaultDomain = computed(() => data.value?.find((a) => a.default));

const formSchema = computed<FormSchema<ISettingsClickDomainsSetDefaultPayload>>(() => ({
  id: {
    type: EFormInputTypes.SELECT,
    label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_CLICK_DOMAINS, 'FORM.DEFAULT_DOMAIN').value,
    options: domainOptions.value,
    value: defaultDomain.value?.id,
  },
}));
</script>

<template>
  <VCard v-data-qa-auto-id="'AdminGeneralSettingsClickDomainsPage'">
    <VLoader :loading="isLoading">
      <VForm
        :schema="formSchema"
        :is-loading="isLoadingSubmission"
        :submit-btn-label="$getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS, 'SAVE')"
        :custom-errors="errors"
        @submit="setDefaultDomain"
      >
        <template #secondaryBtn>
          <VButton
            :to="{ name: ADMIN_GENERAL_SETTINGS_CLICK_DOMAINS_ADD_ROUTE_NAME }"
            :label="$getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_CLICK_DOMAINS, 'ADD_DOMAIN')"
            outlined
          />
        </template>
      </VForm>
    </VLoader>
  </VCard>
</template>
